import { z } from 'zod';
import type { SCRIPT_LENGTHS } from './products/script';
import { revisionSchemeSchema, versionFormatSchema } from './revisions';

const timestamp = z.iso.datetime({ offset: true });

/**
 * Where a version is. `pending` at placement; the worker sets `extracted` once script.txt and layout.json are
 * stored, `processing` while a run works on it, then `processed` or `failed` (`script_version_set_status`,
 * service role); staff set `confirmed` through `staff_confirm_script_version`, and nothing leaves it.
 */
export const SCRIPT_VERSION_STATUSES = [
  'pending',
  'extracted',
  'processing',
  'processed',
  'confirmed',
  'failed',
] as const;
export const scriptVersionStatusSchema = z.enum(SCRIPT_VERSION_STATUSES);
export type ScriptVersionStatus = z.infer<typeof scriptVersionStatusSchema>;

/** What a customer is told of a version: received until staff confirm it, then reviewed. Never a staff status. */
export type CustomerVersionState = 'received' | 'reviewed';
export function customerVersionState(status: ScriptVersionStatus): CustomerVersionState {
  return status === 'confirmed' ? 'reviewed' : 'received';
}

/** A version's label is the customer's own text: shown in the app, never logged or audited. */
export const VERSION_LABEL_MAX = 100;

/**
 * The word count a version must stay under: one length band above the one bought (short takes up to medium,
 * medium and long up to long). Mirrors `version_word_limit` in SQL, which is what decides.
 */
export function versionWordLimit(length: (typeof SCRIPT_LENGTHS)[number]): number {
  return length === 'short' ? 20000 : 50000;
}

/**
 * Every code `script_version_register` refuses with: the worker maps each to an answer and a test reads them from
 * the migration, so a fake repo can give every outcome the SQL can.
 */
export const VERSION_REGISTER_REFUSALS = [
  'project_not_ordered',
  'file_not_found',
  'version_exists',
  'invalid_label',
  'analysis_not_ready',
  'script_unreadable',
  'script_too_long',
  'version_limit_reached',
] as const;
export type VersionRegisterRefusal = (typeof VERSION_REGISTER_REFUSALS)[number];

/** Every code `script_version_delete` refuses with, read from the migration by the same test. */
export const VERSION_DELETE_REFUSALS = [
  'version_not_found',
  'project_not_ordered',
  'version_is_original',
  'version_not_newest',
  'version_confirmed',
  'run_active',
] as const;
export type VersionDeleteRefusal = (typeof VERSION_DELETE_REFUSALS)[number];

/**
 * Every code `staff_confirm_script_version` refuses with (`forbidden` included): the review words each, and a
 * contracts test reads them from the migration.
 */
export const STAFF_CONFIRM_VERSION_REFUSALS = [
  'forbidden',
  'version_not_found',
  'previous_not_confirmed',
  'version_not_processed',
] as const;
export type StaffConfirmVersionRefusal = (typeof STAFF_CONFIRM_VERSION_REFUSALS)[number];

/** A row of `public.scripts`: one per project, created when its script order is placed. */
export const scriptSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  created_at: timestamp,
});
export type Script = z.infer<typeof scriptSchema>;

/**
 * A row of `public.script_versions`. Every version is a `project_files` row of kind `script` (`file_id`), so
 * the PDF stays where the upload landed; the derived `script.txt` and `layout.json` live beside it under
 * `<project>/scripts/<file id>/`. Read by whoever may read the project. A run names the version it works on
 * (`runs.script_version_id`), not the other way round. Version 1 is placement's (no label, no uploader); later ones are
 * the customer's.
 */
export const scriptVersionSchema = z.object({
  id: z.uuid(),
  script_id: z.uuid(),
  project_id: z.uuid(),
  version_number: z.number().int().positive(),
  file_id: z.uuid(),
  status: scriptVersionStatusSchema,
  error: z.string().nullable(),
  confirmed_by: z.uuid().nullable(),
  confirmed_at: timestamp.nullable(),
  label: z.string().min(1).max(VERSION_LABEL_MAX).nullable(),
  created_by: z.uuid().nullable(),
  /** How the version marks its revisions, and whether it is pages or collated: null until a run has recorded them. */
  revision_scheme: revisionSchemeSchema.nullable(),
  revision_format: versionFormatSchema.nullable(),
  created_at: timestamp,
  updated_at: timestamp,
});
export type ScriptVersion = z.infer<typeof scriptVersionSchema>;

/** The folder of a version's derived artefacts: `<project>/scripts/<file id>/` (no rows, like `.harness-logs`). */
export function versionArtefactFolder(projectId: string, fileId: string): string {
  return `${projectId}/scripts/${fileId}`;
}

/** The two artefacts in that folder: the text the agent reads and the layout the resolver uses. */
export const VERSION_ARTEFACT_NAMES = { text: 'script.txt', layout: 'layout.json' } as const;

/** Where a version's artefacts are stored. */
export function versionArtefactPaths(
  projectId: string,
  fileId: string,
): { text: string; layout: string } {
  const folder = versionArtefactFolder(projectId, fileId);
  return {
    text: `${folder}/${VERSION_ARTEFACT_NAMES.text}`,
    layout: `${folder}/${VERSION_ARTEFACT_NAMES.layout}`,
  };
}

/** What `staff_script_review_open` answers: where the PDF is, what it is called, and how long it is. */
export const staffScriptReviewOpenSchema = z.object({
  object_name: z.string().min(1),
  original_filename: z.string().min(1),
  size_bytes: z.number().int().positive(),
  page_count: z.number().int().nonnegative().nullable(),
});
export type StaffScriptReviewOpen = z.infer<typeof staffScriptReviewOpenSchema>;

/**
 * `POST /api/projects/:projectId/versions`: the object the caller has just uploaded, what the file was called, and
 * an optional label (blank is none).
 */
export const uploadVersionBodySchema = z
  .strictObject({
    fileId: z.uuid(),
    filename: z.string().trim().min(1).max(255),
    label: z.string().trim().max(VERSION_LABEL_MAX).optional(),
  })
  .transform(({ label, ...file }) => (label ? { ...file, label } : file));
export type UploadVersionBody = z.infer<typeof uploadVersionBodySchema>;

/** The answer: the new version, pending. */
export const uploadVersionResponseSchema = z.object({ version: scriptVersionSchema });
export type UploadVersionResponse = z.infer<typeof uploadVersionResponseSchema>;
